export interface BrowserCompatResult {
  supported: boolean;
  missing: string[];
}

interface FeatureCheck {
  name: string;
  ok: () => boolean;
  /** FEAT-25: required only by a test whose camera check is on. */
  camera?: true;
}

const FEATURES: FeatureCheck[] = [
  {
    name: "fetch",
    ok: () => typeof window !== "undefined" && typeof window.fetch === "function",
  },
  {
    name: "Promise",
    ok: () => typeof Promise === "function",
  },
  {
    name: "addEventListener",
    ok: () =>
      typeof window !== "undefined" &&
      typeof window.addEventListener === "function",
  },
  {
    name: "crypto.randomUUID",
    ok: () =>
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function",
  },
  {
    name: "Camera (getUserMedia)",
    camera: true,
    ok: () =>
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function",
  },
  {
    name: "localStorage",
    ok: () => {
      try {
        if (typeof window === "undefined" || !window.localStorage) return false;
        const probe = "__compat_probe__";
        window.localStorage.setItem(probe, "1");
        window.localStorage.removeItem(probe);
        return true;
      } catch {
        return false;
      }
    },
  },
];

/**
 * What this browser lacks for THIS test. `camera` says whether the test's
 * camera check is on (FEAT-25): a camera-OFF test never needs the camera API,
 * and a caller that does not know yet must pass `false` and ask again once it
 * does — the Gate re-checks when /preview answers.
 */
export function checkBrowserCompat({
  camera,
}: {
  camera: boolean;
}): BrowserCompatResult {
  const missing: string[] = [];
  for (const feature of FEATURES) {
    if (feature.camera && !camera) continue;
    let ok = false;
    try {
      ok = feature.ok();
    } catch {
      ok = false;
    }
    if (!ok) missing.push(feature.name);
  }
  return { supported: missing.length === 0, missing };
}
